import { Divider, Headline, Layout, ProgressIndicator, Text, TextLink } from '@oneaudi/unified-web-components';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { RegisterOrUpdateConsumptionFootnoteForDialog } from '~/app/compositions/content/calculation-info/components/consumption-footnote';
import { useAppState } from '~/app/contexts/app-context';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { i18nKeys, useI18n } from '~/app/i18n/i18n';
import { AEM_MARKET_CONFIG, useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { DialogProps } from '~/app/pages/summary/dialogs/dialog-props';
import { RateSectionContainerStyles } from '~/app/pages/summary/dialogs/price-detail/core/styles/price-details-style';
import { getFilteredPriceParts } from '~/app/utils/FilteredPriceParts';
import isConsFinancingStage from '~/app/utils/is-cons-financing-stage';
import { Dialog } from '~/components/dialog';
import { useFootnotes } from '~/components/footnotes';
import { CAE_CONFIGUREDCAR_REMARK } from '~/components/footnotes/constants';
import { FootnoteReferenceNgc } from '~/components/footnotes/footnote-reference';
import { RenderHtml } from '~/components/text';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCarlineQuery } from '~/graphql/hooks/use-configured-car-queries';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { state } from '~/storage';

export function PriceDetailsFsagDialog({ open, onClose }: DialogProps) {
	// GB price detail layer
	return (
		<Dialog open={open} onCloseButtonClick={onClose}>
			<PriceDialogFsagContent open={open} onClose={onClose} />
		</Dialog>
	);
}

function PriceDialogFsagContent({ open }: DialogProps) {
	const { catalog } = useConfigurator();
	const { config, market } = useMarketConfig();
	const { i18n } = useI18n();
	const { formatCurrency, formatMonthlyRate } = useCurrencyFormatter();
	const vatConfig = useVATConfig();
	const { priceSummary } = useCatalog();
	const { openDialog, closeDialog } = useDialogs();
	const financialProducts = useAtomValue(state.financialProducts);
	const defaultProduct = useAtomValue(state.defaultFinancialProduct);
	const [preset] = useAtom(state.fsagFinancialCalculatorSettings);

	const { priceDateRemark } = usePriceDetailRemarks();
	const { getReference } = useFootnotes();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const { financialCalculator, carIdentifier } = useConfigurator();
	const culture = `${AEM_MARKET_CONFIG.language}-${AEM_MARKET_CONFIG.market.toUpperCase()}`;
	const { financingData } = useAppState();

	const product = preset ?? { '@ID': defaultProduct };

	// FSAG Summary - official financing content
	useEffect(() => {
		if (!open || financingData?.loading) return;
		setIsLoading(true);
		if (!config.enableFinancingServices) {
			setIsLoading(false);
			return;
		}

		const request = {
			Request: {
				'@Domain': financingData?.financing.meta?.externalRequestOptions['@Domain']
					? financingData?.financing.meta?.externalRequestOptions['@Domain']
					: config.financeDomain,
				'@Culture': culture,
				Vehicle: {
					...financingData?.financing.meta?.externalRequestOptions?.Vehicle,
				},
				Product: product,
			},
		};
		if (!financialCalculator) return;

		const interfaceElement = document.getElementById('interface');

		const calculator = isConsFinancingStage()
			? new financialCalculator({
					WEBCALC_API_URL: 'https://api.cons.webcalc.vwfs.io/webcalc-frontend-service',
					WEBCALC_API_STAGE: 'cons',
				})
			: new financialCalculator();

		calculator.getDefaultSummary(request, {
			ResultContainer: interfaceElement,
			ResponseCallback: () => {
				setIsLoading(false);
			},
			ErrorCallback: () => {
				setIsLoading(false);
				console.error('Financial error Callback triggered.');
			},
		});
	}, [
		open,
		financingData?.loading,
		config.enableFinancingServices,
		config.financeDomain,
		culture,
		JSON.stringify(financingData?.financing?.meta?.externalRequestOptions?.Vehicle),
		JSON.stringify(financialCalculator),
		JSON.stringify(product),
		market,
	]);

	const carlineData = useCarlineQuery(carIdentifier, config.financingInput);
	const carType =
		carlineData?.carline?.configuredCar?.carlineStructureCarline?.vehicleType ||
		carlineData?.carline?.configuredCar?.technicalDataV2?.modelAttributes?.driveTrain;
	const priceDisclaimerText = useMemo(() => {
		if (config.enablePriceDisclaimer) {
			return carType
				? carType === 'BEV'
					? i18n('ngc.remarks.price.legalBEV')
					: i18n('ngc.remarks.price.legalICE')
				: undefined;
		}
		return null;
	}, [config.enablePriceDisclaimer, carType, i18n]);

	if (config.showCaESummaryText && !config.hideConsumptionEmissionEngineName) {
		RegisterOrUpdateConsumptionFootnoteForDialog();
	}

	const priceParts = getFilteredPriceParts(priceSummary, config);

	return (
		<div className="flex flex-col gap-8">
			{/* eslint-disable-next-line */}
			<div tabIndex={0} className="flex flex-col gap-2">
				<Headline tag="h2" variant={{ type: 'headline', order: '1', weight: 'bold' }}>
					{catalog.carInfo?.carline}
				</Headline>
				<Headline tag="h3" variant={{ type: 'headline', order: '3', weight: 'normal' }}>
					{catalog.carInfo?.model}
					&nbsp;
					<FootnoteReferenceNgc
						reference={getReference(CAE_CONFIGUREDCAR_REMARK)}
						ariaLabel={i18n('ngc.a11y.footnotes.cae')}
					/>
				</Headline>
			</div>

			{/* eslint-disable-next-line */}
			<div tabIndex={0} className="flex flex-col">
				<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>{i18n('ngc.priceDetails.title')}</Text>
			</div>

			<ul className="flex flex-col gap-6">
				{priceParts?.map((part) => (
					<PriceSectionFsag
						key={part.type}
						pricePartType={part.type}
						title={i18n(`ngc.priceDetails.${part.type}` as keyof typeof i18nKeys)}
						price={formatCurrency(part.price, vatConfig.priceParts[part.type])}
						disclaimer={null}
					/>
				))}
			</ul>

			<Divider />

			<PriceSectionFsag
				pricePartType="total"
				title={i18n(`ngc.priceDetails.${config.uiPreferences.prices.totalPriceFrom}`)}
				price={formatCurrency(catalog.priceSummary?.totalPrice, vatConfig.totalPrice)}
				bold
				disclaimer={priceDisclaimerText}
			/>
			{catalog?.priceSummary?.monthlyRate &&
				catalog.priceSummary.monthlyRate.__typename === 'MonthlyRate' &&
				config.enableFinancingServices && (
					<>
						<div className="flex flex-col">
							{isLoading ? (
								<ProgressIndicator variant={'spinner'} type={'infinite'} />
							) : (
								<>
									<RateSectionContainerStyles>
										<PriceSectionFsag
											pricePartType="totalRate"
											title={
												financialProducts.find((prod) => {
													return product && prod.key === product['@ID'];
												})?.val ?? i18n('ngc.priceDetails.privateLeasing')
											}
											price={formatMonthlyRate(catalog.priceSummary?.monthlyRate.rawValue)}
											bold
											disclaimer={null}
										/>
										<div className="flex flex-row">
											<TextLink
												size="sm"
												iconPosition="trailing"
												icon={'forward'}
												onClick={() => {
													closeDialog('priceDetails');
													openDialog('monthlyPriceRatesFsag');
												}}
												variant="standalone">
												{i18n('ngc.actions.changeLeasingRate')}
											</TextLink>
										</div>
									</RateSectionContainerStyles>
								</>
							)}
							<div id="interface" />
						</div>
					</>
				)}
			{priceDateRemark && (
				<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>
					{}
					<RenderHtml tabIndex={0} html={priceDateRemark} />
				</Text>
			)}
		</div>
	);
}

export function PriceSectionFsag({
	pricePartType,
	title,
	price,
	bold,
	disclaimer,
}: {
	pricePartType: string;
	/** Translated title of this section.  */
	title: string;
	/** Formatted price of this part. */
	price: string | null | undefined;
	bold?: boolean;
	disclaimer: string | null | undefined;
}) {
	const { i18n } = useI18n();
	const { addFootnote, getReference } = useFootnotes();

	const i18nKey = `ngc.remarks.pricePart.${pricePartType}` as keyof typeof i18nKeys;

	if (i18nKey in i18nKeys) {
		addFootnote(i18nKey, i18n(i18nKey));
	}

	if (disclaimer) {
		addFootnote('p-disclaimer', disclaimer);
	}

	return (
		/* eslint-disable-next-line */
		<div tabIndex={0}>
			<Layout direction={{ default: 'column', md: 'row' }} columnGap="32px" justify="space-between">
				<Text variant={{ type: 'copy', order: '2', style: bold ? 'bold' : 'normal' }}>
					{title}

					<FootnoteReferenceNgc
						reference={getReference(i18nKey)}
						ariaLabel={i18n('ngc.a11y.footnotes.price.part', { part: title })}
					/>

					<FootnoteReferenceNgc
						reference={getReference('p-disclaimer')}
						ariaLabel={i18n('ngc.a11y.footnotes.price.disclaimer')}
					/>
				</Text>
				<Text variant={{ type: 'copy', order: '2', style: bold ? 'bold' : 'normal' }}>{price}</Text>
			</Layout>
		</div>
	);
}

/**
 * Returns the remark text to be shown in the bottom of the price details layer.
 */
function usePriceDetailRemarks(): {
	priceDateRemark: string | undefined | null;
} {
	const { priceSummary } = useCatalog();

	return {
		priceDateRemark: priceSummary?.priceDate?.text,
	};
}
